import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ampli } from '@/ampli';
import { useTransition, useSpring } from '@react-spring/web';
import Link from 'next/link';
import { Route } from 'nextjs-routes';
import React, { useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { FeedbackModal } from '@/components/Modal/FeedBackFormModal';

import { useFeature } from '@/hooks/useFeature';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useMeasure } from '@/hooks/useMeasure';

import { useCurrencyStore, selectedCurrencySelector } from '@/store/currency';

import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { ChartCandleIcon } from '@/ui/Icons/ChartCandleIcon';
import { ChevronLeftIcon } from '@/ui/Icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/ui/Icons/ChevronRightIcon';
import { DarkModeIcon } from '@/ui/Icons/DarkModeIcon';
import { FeedbackIcon } from '@/ui/Icons/FeedbackIcon';
import { GlobeIcon } from '@/ui/Icons/GlobeIcon';
import { InboxIcon } from '@/ui/Icons/InboxIcon';
import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import { SwapIcon } from '@/ui/Icons/SwapIcon';
import { TradeHistoryIcon } from '@/ui/Icons/TradeHistoryIcon';
import { WalletIcon } from '@/ui/Icons/WalletIcon';

import { AnimatedDiv } from '@/utils/animated-div';
import { classNames } from '@/utils/classnames';
import { HOMEPAGE_REVAMP, PERPS } from '@/utils/featureFlags';
import { ensurePylonSession, showPylonChat } from '@/utils/pylon';
import { routes } from '@/utils/routes';

import { DarkmodeTogglePage } from '../DarkmodeToggle';
import { NavigationDropdownList } from '../NavigationDropdownList';
import { AdditionalOptionsTrigger } from './AdditionalOptionsTrigger';
import { CurrencySelectorPage } from './CurrencySelectorPage';

enum STEPS {
  MAIN = 1,
  LEARN_MORE = 2,
  THEME = 3,
  CURRENCY = 4,
}

interface AdditionalOptionsDropdownProps {
  onOpen?: () => void;
  showPulsingDot?: boolean;
  onTradeHistoryClick: (event: React.MouseEvent) => void;
  onPortfolioClick: (event: React.MouseEvent) => void;
  tradePage: Route;
}

export const AdditionalOptionsDropdown: React.FC<AdditionalOptionsDropdownProps> = ({
  onOpen,
  showPulsingDot,
  onTradeHistoryClick,
  onPortfolioClick,
  tradePage,
}) => {
  const popoverStyles = popover();
  const [currentStep, setCurrentStep] = useState(STEPS.MAIN);
  const [clickedOn, setClickedOn] = useState<STEPS | null>(null);
  const [isClosingLearnMore, setIsClosingLearnMore] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'forward' | 'backward'>('forward');
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const learnMoreButtonRef = useRef<HTMLDivElement>(null!);
  const appearanceButtonRef = useRef<HTMLDivElement>(null!);
  const currencyButtonRef = useRef<HTMLDivElement>(null!);
  const closingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isMobile = useMedia(breakpoints.mobile, false);
  const selectedCurrency = useCurrencyStore(selectedCurrencySelector);
  const { isConnected } = useMatchaWallets();

  const transitions = useTransition(currentStep, {
    key: currentStep,
    from: {
      opacity: 0,
      transform: slideDirection === 'forward' ? 'translateX(100px)' : 'translateX(-100px)',
    },
    enter: {
      opacity: 1,
      transform: 'translateX(0px)',
    },
    leave: {
      opacity: 0,
      transform: slideDirection === 'forward' ? 'translateX(-100px)' : 'translateX(100px)',
      position: 'absolute',
    },
    config: { tension: 225, friction: 30, duration: 200 },
  });

  const { bounds, nodeRef: contentRef } = useMeasure<HTMLDivElement>();

  const [springs, api] = useSpring(() => ({
    height: 0,
    config: { tension: 450, friction: 25, duration: 200, mass: 0.6, clamp: true },
  }));

  useEffect(() => {
    api.start({ height: bounds.height });
  }, [bounds.height, api]);

  function handleOpenChange(open: boolean) {
    setIsDropdownOpen(open);

    if (open) {
      // Early load and initialize Pylon to avoid delay
      // on contact-support click
      void ensurePylonSession().catch(() => undefined);
      onOpen?.();
      return;
    }

    setCurrentStep(STEPS.MAIN);
    setClickedOn(null);
    setIsClosingLearnMore(false);
  }

  function makeSelectHandler(step: STEPS) {
    return (e: Pick<Event, 'preventDefault'>) => {
      e.preventDefault();
      setSlideDirection('forward');
      setClickedOn(step);
      setCurrentStep(step);
    };
  }

  function handleGoBack(e: React.MouseEvent | React.KeyboardEvent) {
    e.preventDefault();
    setIsClosingLearnMore(true);
    setSlideDirection('backward');

    closingTimeoutRef.current = setTimeout(() => {
      setCurrentStep(STEPS.MAIN);
      setIsClosingLearnMore(false);
    }, 100);
  }

  // Cleanup any timeouts on unmount
  useEffect(() => {
    return () => {
      if (closingTimeoutRef.current) {
        clearTimeout(closingTimeoutRef.current);
      }
    };
  }, []);

  // When going back to MAIN, restore focus to the "Learn More" button
  useEffect(() => {
    if (currentStep === STEPS.MAIN && clickedOn) {
      if (clickedOn === STEPS.LEARN_MORE && learnMoreButtonRef.current) {
        learnMoreButtonRef.current.focus();
      } else if (clickedOn === STEPS.THEME && appearanceButtonRef.current) {
        appearanceButtonRef.current.focus();
      } else if (clickedOn === STEPS.CURRENCY && currencyButtonRef.current) {
        currencyButtonRef.current.focus();
      }
      setClickedOn(null);
    }
  }, [currentStep, clickedOn]);

  const handleOpenFeedbackModal = () => {
    ampli.feedbackModalDisplayed();
    setIsFeedbackModalOpen(true);
    setIsDropdownOpen(false);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  return (
    <>
      <DropdownMenu.Root onOpenChange={handleOpenChange} open={isDropdownOpen} modal={false}>
        <AdditionalOptionsTrigger
          isOpen={isDropdownOpen}
          isMobile={isMobile}
          showPulsingDot={showPulsingDot}
        />
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            forceMount
            className={classNames(
              popoverStyles.base(),
              'bg-special-background-modal relative overflow-hidden transition-[height] duration-300 ease-in',
            )}
          >
            <AnimatedDiv
              style={{
                position: 'relative',
                overflow: 'hidden',
                height: springs.height as unknown as number | string,
              }}
            >
              <div ref={contentRef}>
                {transitions((style, step) => (
                  <AnimatedDiv style={style as unknown as React.CSSProperties}>
                    {step === STEPS.MAIN ? (
                      <MainMenuContent
                        isMobile={isMobile}
                        onSelectLearnMore={makeSelectHandler(STEPS.LEARN_MORE)}
                        onSelectAppearance={makeSelectHandler(STEPS.THEME)}
                        onSelectCurrency={makeSelectHandler(STEPS.CURRENCY)}
                        learnMoreButtonRef={learnMoreButtonRef}
                        appearanceButtonRef={appearanceButtonRef}
                        currencyButtonRef={currencyButtonRef}
                        selectedCurrency={selectedCurrency}
                        isConnected={isConnected}
                        onOpenFeedbackModal={handleOpenFeedbackModal}
                        onTradeHistoryClick={onTradeHistoryClick}
                        onPortfolioClick={onPortfolioClick}
                        tradePage={tradePage}
                      />
                    ) : step === STEPS.THEME ? (
                      <DarkmodeTogglePage onGoBack={handleGoBack} />
                    ) : step === STEPS.CURRENCY ? (
                      <CurrencySelectorPage onGoBack={handleGoBack} />
                    ) : (
                      <LearnMoreMenuContent
                        onGoBack={handleGoBack}
                        isClosingLearnMore={isClosingLearnMore}
                      />
                    )}
                  </AnimatedDiv>
                ))}
              </div>
            </AnimatedDiv>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        triggerSource="menu"
      />
    </>
  );
};

interface MainMenuContentProps {
  isMobile: boolean;
  onSelectLearnMore: (e: Pick<Event, 'preventDefault'>) => void;
  onSelectAppearance: (e: Pick<Event, 'preventDefault'>) => void;
  onSelectCurrency: (e: Pick<Event, 'preventDefault'>) => void;
  learnMoreButtonRef: React.RefObject<HTMLDivElement>;
  appearanceButtonRef: React.RefObject<HTMLDivElement>;
  currencyButtonRef: React.RefObject<HTMLDivElement>;
  selectedCurrency: string;
  isConnected: boolean;
  onOpenFeedbackModal: () => void;
  onTradeHistoryClick: (event: React.MouseEvent) => void;
  onPortfolioClick: (event: React.MouseEvent) => void;
  tradePage: Route;
}

function MainMenuContent({
  onSelectAppearance,
  onSelectLearnMore,
  onSelectCurrency,
  learnMoreButtonRef,
  appearanceButtonRef,
  currencyButtonRef,
  selectedCurrency,
  isConnected,
  onOpenFeedbackModal,
  onTradeHistoryClick,
  onPortfolioClick,
  tradePage,
}: MainMenuContentProps) {
  const { item, icon } = popover();
  const homepageRevamp = useFeature(HOMEPAGE_REVAMP);
  const perpsEnabled = useFeature(PERPS);

  return (
    <div>
      {homepageRevamp ? (
        <DropdownMenu.Item
          className="header-sm:hidden outline-none"
          onClick={() => ampli.navTokensClicked()}
        >
          <Link href={routes.TOKENS} className={item()}>
            <GlobeIcon className={icon()} />
            Tokens
          </Link>
        </DropdownMenu.Item>
      ) : (
        <>
          <DropdownMenu.Item
            className="header-sm:hidden outline-none"
            onClick={() => ampli.navDiscoverClicked()}
          >
            <Link href={routes.INDEX} className={item()}>
              <GlobeIcon className={icon()} />
              Discover
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="header-sm:hidden outline-none"
            onClick={() => ampli.navTradeClicked()}
          >
            <Link href={tradePage} className={item()}>
              <SwapIcon className={icon()} />
              Trade
            </Link>
          </DropdownMenu.Item>
        </>
      )}
      <DropdownMenu.Item className="header-sm:hidden outline-none">
        <Link href={routes.PORTFOLIO} className={item()} onClick={onPortfolioClick}>
          <WalletIcon className={icon()} />
          Portfolio
        </Link>
      </DropdownMenu.Item>
      <DropdownMenu.Item className="header-sm:hidden outline-none">
        <Link href={routes.TRADE_HISTORY} className={item()} onClick={onTradeHistoryClick}>
          <TradeHistoryIcon className={icon()} />
          History
        </Link>
      </DropdownMenu.Item>
      {perpsEnabled && (
        <DropdownMenu.Item className="header-sm:hidden outline-none">
          <Link href={routes.PERPS} className={item()}>
            <ChartCandleIcon className={icon()} />
            Perps
          </Link>
        </DropdownMenu.Item>
      )}
      <hr className="h-px border-t border-special-border-divider border-solid mx-1.5 my-2 header-sm:hidden" />
      <DropdownMenu.Item className={item()} ref={appearanceButtonRef} onClick={onSelectAppearance}>
        <DarkModeIcon className={icon()} />
        Appearance
        <ChevronRightIcon className="text-token-gray-1600 ml-auto mr-2" />
      </DropdownMenu.Item>
      <DropdownMenu.Item className={item()} ref={currencyButtonRef} onClick={onSelectCurrency}>
        <GlobeIcon className={icon()} />
        Currency
        <span className="text-token-gray-800 ml-auto mr-2">{selectedCurrency}</span>
        <ChevronRightIcon className="text-token-gray-1600" />
      </DropdownMenu.Item>

      <DropdownMenu.Item className={item()} onClick={showPylonChat}>
        <FeedbackIcon className={icon()} />
        Contact Support
      </DropdownMenu.Item>

      {isConnected && (
        <DropdownMenu.Item className={item()} onClick={onOpenFeedbackModal}>
          <InboxIcon className={icon()} />
          Give Feedback
        </DropdownMenu.Item>
      )}
      <>
        <hr className="h-px border-t border-special-border-divider border-solid mx-1.5 my-2" />
        <DropdownMenu.Item className={item()} onSelect={onSelectLearnMore} ref={learnMoreButtonRef}>
          <InfoIconOutline className={icon()} />
          <span className="flex-1">Learn more</span>
          <ChevronRightIcon className="text-token-gray-1600 ml-auto mr-2" />
        </DropdownMenu.Item>
      </>
    </div>
  );
}

interface LearnMoreMenuContentProps {
  onGoBack: (e: React.MouseEvent | React.KeyboardEvent) => void;
  isClosingLearnMore: boolean;
}

function LearnMoreMenuContent({ onGoBack, isClosingLearnMore }: LearnMoreMenuContentProps) {
  return (
    <div className={isClosingLearnMore ? 'closing' : ''}>
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            'p-1',
            'flex',
            'mx-0.5',
            'mt-0.5',
            'items-center',
            'gap-1',
            'w-full',
            'rounded-md',
            'font-semibold',
          ],
        })}
      >
        <button
          className={classNames(
            'text-token-gray-1600',
            'w-7',
            'h-7',
            'flex',
            'items-center',
            'justify-center',
            'rounded-sm',
            'hover:bg-token-gray-200',
            'focus-visible:bg-token-gray-200',
            'focus-visible:outline-none',
          )}
          aria-label="Go back"
          onClick={onGoBack}
        >
          <ChevronLeftIcon />
        </button>
        Learn more
      </div>

      <NavigationDropdownList />
    </div>
  );
}
